import React, { useEffect, useRef, useState } from "react";

/* ───────────────────────────────────────────────
   Reveal — scroll-triggered fade/slide-in wrapper.
   Falls back to visible if IntersectionObserver
   is unavailable or reduced-motion is preferred.
   ─────────────────────────────────────────────── */
export function Reveal({
  children,
  as: Tag = "div",
  delay = 0,
  className = "",
  threshold = 0.15,
  ...rest
}) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReduced || !("IntersectionObserver" in window)) {
      setShown(true);
      return;
    }
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShown(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold, rootMargin: "0px 0px -40px 0px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);

  return (
    <Tag
      ref={ref}
      style={{ transitionDelay: shown ? `${delay}ms` : "0ms" }}
      className={`${shown ? "reveal-in" : "reveal-init"} ${className}`}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/* ───────────────────────────────────────────────
   Eyebrow — small uppercase section label
   ─────────────────────────────────────────────── */
export function Eyebrow({ children, className = "" }) {
  return <p className={`eyebrow ${className}`}>{children}</p>;
}

/* ───────────────────────────────────────────────
   SectionHeading — eyebrow + title + optional sub
   ─────────────────────────────────────────────── */
export function SectionHeading({
  eyebrow,
  title,
  subtitle,
  align = "center",
  id,
  className = "",
}) {
  const alignment =
    align === "center" ? "text-center mx-auto items-center" : "text-left items-start";
  return (
    <div className={`flex flex-col gap-4 ${alignment} ${className}`}>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h2
        id={id}
        className="text-3xl sm:text-4xl lg:text-[2.6rem] font-bold tracking-tight text-zinc-900 leading-[1.1] max-w-3xl"
      >
        {title}
      </h2>
      {subtitle && (
        <p className="text-zinc-600 text-base sm:text-lg leading-relaxed max-w-2xl">
          {subtitle}
        </p>
      )}
    </div>
  );
}

/* ───────────────────────────────────────────────
   Button — variants: primary | secondary | ghost
   Renders <a> when href present, else <button>.
   ─────────────────────────────────────────────── */
export function Button({
  children,
  variant = "primary",
  size = "md",
  href,
  className = "",
  ...rest
}) {
  const variants = {
    primary: "btn-primary",
    secondary: "btn-secondary",
    ghost: "btn-ghost",
  };
  const sizes = { sm: "btn-sm", md: "btn-md", lg: "btn-lg" };
  const cls = `btn ${variants[variant] || variants.primary} ${sizes[size] || sizes.md} ${className}`;

  if (href) {
    return (
      <a href={href} className={cls} {...rest}>
        {children}
      </a>
    );
  }
  return (
    <button className={cls} {...rest}>
      {children}
    </button>
  );
}

/* ───────────────────────────────────────────────
   Badge — modern pill
   ─────────────────────────────────────────────── */
export function Badge({ children, className = "" }) {
  return <span className={`badge ${className}`}>{children}</span>;
}

/* ───────────────────────────────────────────────
   Card — glass surface, optional interactive hover
   ─────────────────────────────────────────────── */
export function Card({ children, interactive = false, className = "", ...rest }) {
  return (
    <div className={`${interactive ? "card-interactive" : "card"} ${className}`} {...rest}>
      {children}
    </div>
  );
}

/* ───────────────────────────────────────────────
   PageGlow — reusable hero background
   (dotted grid + soft gradient orbs)
   ─────────────────────────────────────────────── */
export function PageGlow({ className = "" }) {
  return (
    <div className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`} aria-hidden="true">
      <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-60" />
      <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[680px] h-[680px] rounded-full bg-accent-500/[0.12] blur-[120px] animate-pulse-glow" />
      <div className="absolute top-1/3 right-0 w-[420px] h-[420px] rounded-full bg-sky-400/[0.12] blur-[120px]" />
      <div className="absolute bottom-0 left-0 w-[420px] h-[420px] rounded-full bg-emerald-500/[0.12] blur-[120px]" />
      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-zinc-900/10 to-transparent" />
    </div>
  );
}

/* ───────────────────────────────────────────────
   AnimatedCounter — counts up when scrolled into view
   ─────────────────────────────────────────────── */
export function AnimatedCounter({ value, className = "" }) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(value);

  useEffect(() => {
    // Parse a leading number out of strings like "5M+", "30+", "15"
    const match = String(value).match(/^(\D*)([\d.]+)(.*)$/);
    if (!match) {
      setDisplay(value);
      return;
    }
    const prefix = match[1];
    const target = parseFloat(match[2]);
    const suffix = match[3];
    const decimals = (match[2].split(".")[1] || "").length;

    const prefersReduced =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced || !("IntersectionObserver" in window)) {
      setDisplay(value);
      return;
    }

    const node = ref.current;
    let raf;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          const start = performance.now();
          const duration = 1100;
          const tick = (now) => {
            const p = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            const current = (target * eased).toFixed(decimals);
            setDisplay(`${prefix}${current}${suffix}`);
            if (p < 1) raf = requestAnimationFrame(tick);
          };
          raf = requestAnimationFrame(tick);
          observer.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    if (node) observer.observe(node);
    return () => {
      observer.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [value]);

  return (
    <span ref={ref} className={className}>
      {display}
    </span>
  );
}
